import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, renderSlot as _renderSlot } from "vue";
const _hoisted_1 = { key: 0 };
const _hoisted_2 = {
    key: 0,
    class: "text-left",
    "data-cy": "productPriceBooking"
};
const _hoisted_3 = {
    key: 0,
    class: "text-left",
    "data-cy": "productPriceResource"
};
const _hoisted_4 = { class: "v-col-12 pa-0 pb-2" };
const _hoisted_5 = { class: "v-col-12 pa-0 pb-2" };
import { getResourceGroups, getResources } from "@/model/Product";
import KrumosCalendar from "@/components/KrumosCalendar.vue";
import { computed, inject, onMounted, reactive, ref, watch } from "vue";
import { CartItem } from "@/model/CartItem";
import { PriceType } from "@/model/enum/PriceType.model";
import { BookingServiceSymbol } from "@/InjectionSymbols";
import { validationRules } from "@/ValidationRules";
import { MapDeserializer } from "@/util/MapDeserializer";
import { Formatter } from "@/util/Formatter";
import { DayOfWeek } from "@js-joda/core";
import { ResourceType } from "@/model/enum/ResourceType";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProductMetaContainer',
    props: {
        product: { type: Object, required: true },
        orderItem: { type: Object, required: false }
    },
    emits: ['buttonClick'],
    setup(__props, { emit: __emit }) {
        const bookingService = inject(BookingServiceSymbol);
        const props = __props;
        const emit = __emit;
        let cartItem = reactive(new CartItem(props.product));
        let bookingPriceWrapper = ref(new Map());
        let availabilitiesWrapper = reactive({});
        let initialDataLoaded = ref(false);
        let form = ref(null);
        let maxBookablePersons = ref(Number.MAX_VALUE);
        const selectedDateDisplay = computed(() => {
            let display = "Gewähltes Datum: " + cartItem.dateFrom.toLocaleDateString();
            if (cartItem.dateFrom.toLocaleDateString() != cartItem.dateTo.toLocaleDateString()) {
                display += " bis " + cartItem.dateTo.toLocaleDateString();
            }
            return display;
        });
        const filteredTimeslots = computed(() => {
            if (!props.product.timeslots || props.product.timeslots.length === 0) {
                return [];
            }
            // Convert JavaScript Date day (0-6, Sunday-Saturday) to JS-Joda DayOfWeek (1-7, Monday-Sunday)
            const jsDay = cartItem.dateFrom.getDay();
            // JS: 0=Sunday, 1=Monday, ..., 6=Saturday
            // JS-Joda: 1=Monday, ..., 7=Sunday
            const jodaDay = jsDay === 0 ? DayOfWeek.SUNDAY : DayOfWeek.of(jsDay);
            // Filter timeslots to only include those that match the selected day of week
            return props.product.timeslots.filter(timeslot => timeslot.weekdays.some(day => day == jodaDay));
        });
        onMounted(() => {
            if (props.orderItem) {
                // Set dates from orderItem
                cartItem.dateFrom = new Date(props.orderItem.dateFrom);
                cartItem.dateTo = new Date(props.orderItem.dateTo);
                // Copy reservations from orderItem to cartItem.resourceReservations
                for (const resourceId in props.orderItem.reservations) {
                    cartItem.resourceReservations[resourceId] = props.orderItem.reservations[resourceId];
                }
                // Find and set the selected timeslot if applicable
                if (props.orderItem.timeslotId) {
                    const matchingTimeslot = props.product.timeslots.find(slot => slot.id === props.orderItem?.timeslotId);
                    if (matchingTimeslot) {
                        cartItem.selectedTimeslot = matchingTimeslot;
                    }
                }
            }
            onDateSelected(cartItem.dateFrom, cartItem.dateTo);
        });
        watch(() => props.product, (newValue) => {
            // Reset everything when product changes
            cartItem = reactive(new CartItem(newValue));
            bookingPriceWrapper = ref(new Map());
            availabilitiesWrapper = reactive({});
            initialDataLoaded.value = false;
            onDateSelected(cartItem.dateFrom, cartItem.dateTo);
        });
        // Watch for timeslot selection (for timeslot products)
        watch(() => cartItem.selectedTimeslot, (newSlot) => {
            if (newSlot) {
                loadAvailabilities();
            }
        });
        // Watch for changes in filtered timeslots (when date changes)
        watch(filteredTimeslots, (newFilteredTimeslots) => {
            // If there's a selected timeslot but it's not in the filtered list, clear it
            if (cartItem.selectedTimeslot && newFilteredTimeslots.length > 0) {
                const isTimeslotValid = newFilteredTimeslots.some(timeslot => timeslot.id === cartItem.selectedTimeslot?.id);
                if (!isTimeslotValid) {
                    cartItem.selectedTimeslot = undefined;
                }
            }
            else if (cartItem.selectedTimeslot && newFilteredTimeslots.length === 0) {
                // If there are no timeslots available for the selected day, clear the selection
                cartItem.selectedTimeslot = undefined;
            }
        });
        function loadAvailabilities() {
            let from = new Date(cartItem.dateFrom);
            let to = new Date(cartItem.dateTo);
            if (props.product.timeslots && props.product.timeslots.length > 0) {
                if (!cartItem.selectedTimeslot) {
                    initialDataLoaded.value = true;
                    return;
                }
                const slot = cartItem.selectedTimeslot;
                from.setHours(slot.resourceOccupationStart.hour(), slot.resourceOccupationStart.minute(), 0, 0);
                to.setHours(slot.resourceOccupationEnd.hour(), slot.resourceOccupationEnd.minute(), 0, 0);
            }
            else {
                if (props.product.priceStructure.pricedPerNight) {
                    from.setHours(12, 0, 0, 0);
                    to.setHours(11, 59, 59);
                }
                else {
                    from.setHours(0, 0, 0, 0);
                    to.setHours(23, 59, 59);
                }
            }
            bookingService.loadAvailabilitiesForProductSimple(props.product.id, from, to, props.orderItem?.id)
                .then(availabilities => onAvailabilitiesLoaded(availabilities));
        }
        function onAvailabilitiesLoaded(availabilities) {
            for (let availabilitiesKey in availabilities) {
                delete availabilitiesWrapper[availabilitiesKey];
            }
            Object.assign(availabilitiesWrapper, availabilities);
            updateMaxBookablePersons();
            initialDataLoaded.value = true;
        }
        function updateMaxBookablePersons() {
            // Calculate the max number of available persons
            // This is left over from when we still used meta data and currently has no effect. However, it will
            // be nedded again when it is possible to link resources to each other
            let maximumPersonAvailability = Number.MAX_VALUE;
            maxBookablePersons.value = maximumPersonAvailability;
        }
        function onDateSelected(dateFrom, dateTo) {
            cartItem.dateFrom = dateFrom;
            cartItem.dateTo = dateTo;
            requestPrice();
            loadAvailabilities();
        }
        function requestPrice() {
            bookingService.requestPrice(cartItem.dateFrom.toISOString(), cartItem.dateTo.toISOString(), props.product.id)
                .then(response => {
                bookingPriceWrapper.value = MapDeserializer.deserializePriceTypeMap(response.data);
            });
        }
        function get_booking_price(priceType, referenceId) {
            if (bookingPriceWrapper.value.has(priceType)) {
                const priceMap = bookingPriceWrapper.value.get(priceType);
                const price = priceMap.get(referenceId).amount;
                if (price && price > 0)
                    return price.toFixed(2) + " €";
            }
            return "";
        }
        function getResourceSelection(resourceId) {
            let maxAvailable = availabilitiesWrapper[Number(resourceId)] ?? 0;
            return [...Array(maxAvailable + 1).keys()];
        }
        function getResourceName(resourceId) {
            for (const resourceGroup of props.product.resourceGroups) {
                const resource = resourceGroup.resources.find(resource => resource.id === resourceId);
                if (resource) {
                    return resource.name;
                }
            }
            const resource = props.product.resources.find(resource => resource.id === resourceId);
            return resource ? resource.name : undefined;
        }
        function onResourceSelected(resource, modelValue) {
            cartItem.resourceReservations[Number(resource.id)] = modelValue;
        }
        function click_button() {
            form.value.resetValidation();
            form.value.validate().then((result) => {
                if (result.valid) {
                    emit('buttonClick', cartItem);
                }
            });
        }
        return (_ctx, _cache) => {
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_container = _resolveComponent("v-container");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_form = _resolveComponent("v-form");
            return (_openBlock(), _createBlock(_component_v_container, {
                class: "pa-2",
                id: "calendarWrapper"
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_form, {
                        ref_key: "form",
                        ref: form
                    }, {
                        default: _withCtx(() => [
                            _createVNode(KrumosCalendar, {
                                class: "d-block w-auto",
                                "data-cy": "productMetaCalendar",
                                product: __props.product,
                                "init-date-from": _unref(cartItem).dateFrom,
                                "init-date-to": _unref(cartItem).dateTo,
                                onDateSelected: onDateSelected
                            }, null, 8, ["product", "init-date-from", "init-date-to"]),
                            (filteredTimeslots.value.length > 0)
                                ? (_openBlock(), _createBlock(_component_v_container, {
                                    key: 0,
                                    class: "pa-2"
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_select, {
                                            modelValue: _unref(cartItem).selectedTimeslot,
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(cartItem).selectedTimeslot) = $event)),
                                            items: filteredTimeslots.value,
                                            "item-title": _unref(Formatter).timeslot,
                                            "item-value": "id",
                                            label: "Zeitslot wählen",
                                            "return-object": true,
                                            "menu-props": { maxHeight: '300' },
                                            "data-cy": "timeslot-select",
                                            variant: "outlined",
                                            rules: [_unref(validationRules).required]
                                        }, null, 8, ["modelValue", "items", "item-title", "rules"])
                                    ]),
                                    _: 1
                                }))
                                : _createCommentVNode("", true),
                            _createVNode(_component_v_container, { class: "pa-2" }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_container, { class: "d-flex justify-start pa-0" }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(selectedDateDisplay.value), 1)
                                        ]),
                                        _: 1
                                    }),
                                    _createVNode(_component_v_container, { class: "d-flex justify-start pa-0" }, {
                                        default: _withCtx(() => _cache[1] || (_cache[1] = [
                                            _createTextVNode("Preise für gewählten Zeitraum:")
                                        ])),
                                        _: 1
                                    }),
                                    _createVNode(_component_v_container, { class: "justify-start" }, {
                                        default: _withCtx(() => [
                                            (_unref(bookingPriceWrapper).size > 0)
                                                ? (_openBlock(), _createElementBlock("ul", _hoisted_1, [
                                                    (__props.product.priceStructure.isPricingByFlatrate() && get_booking_price(_unref(PriceType).FLAT_RATE, null) != '')
                                                        ? (_openBlock(), _createElementBlock("li", _hoisted_2, "Pro Buchung, Pauschal: " + _toDisplayString(get_booking_price(_unref(PriceType).FLAT_RATE, null)), 1))
                                                        : _createCommentVNode("", true),
                                                    (__props.product.priceStructure.isPricingByResources())
                                                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(bookingPriceWrapper).get(_unref(PriceType).PER_RESOURCE), (idPriceArray, index) => {
                                                            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                                                                (get_booking_price(_unref(PriceType).PER_RESOURCE, idPriceArray[0]) != '')
                                                                    ? (_openBlock(), _createElementBlock("li", _hoisted_3, " Pro " + _toDisplayString(getResourceName(idPriceArray[0])) + ": " + _toDisplayString(get_booking_price(_unref(PriceType).PER_RESOURCE, idPriceArray[0])), 1))
                                                                    : _createCommentVNode("", true)
                                                            ], 64));
                                                        }), 128))
                                                        : _createCommentVNode("", true)
                                                ]))
                                                : (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                    key: 1,
                                                    indeterminate: ""
                                                }))
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_container, { class: "pa-0" }, {
                                default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getResources)(__props.product, _unref(ResourceType).ATTRIBUTE), (resource) => {
                                        return (_openBlock(), _createBlock(_component_v_text_field, {
                                            key: resource.id,
                                            class: "v-col-12 compact pb-1",
                                            variant: "outlined",
                                            density: "compact",
                                            label: resource.name,
                                            modelValue: _unref(cartItem).resourceReservations[resource.id],
                                            "onUpdate:modelValue": ($event) => ((_unref(cartItem).resourceReservations[resource.id]) = $event),
                                            rules: [_unref(validationRules).nonNegativeInteger],
                                            "data-cy": resource.name
                                        }, null, 8, ["label", "modelValue", "onUpdate:modelValue", "rules", "data-cy"]));
                                    }), 128)),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getResourceGroups)(__props.product, _unref(ResourceType).ATTRIBUTE), (resourceGroup) => {
                                        return (_openBlock(), _createBlock(_component_v_container, {
                                            class: "d-flex flex-wrap pa-0 pb-2",
                                            key: resourceGroup.id
                                        }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("h4", _hoisted_4, _toDisplayString(resourceGroup.name), 1),
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resourceGroup.resources, (resource) => {
                                                    return (_openBlock(), _createBlock(_component_v_text_field, {
                                                        key: resource.id,
                                                        class: "v-col-12 compact pb-1 v-col-sm-6 v-col-md-12 v-col-lg-6",
                                                        variant: "outlined",
                                                        density: "compact",
                                                        label: resource.name,
                                                        modelValue: _unref(cartItem).resourceReservations[resource.id],
                                                        "onUpdate:modelValue": ($event) => ((_unref(cartItem).resourceReservations[resource.id]) = $event),
                                                        rules: [_unref(validationRules).nonNegativeInteger],
                                                        "data-cy": resource.name
                                                    }, null, 8, ["label", "modelValue", "onUpdate:modelValue", "rules", "data-cy"]));
                                                }), 128))
                                            ]),
                                            _: 2
                                        }, 1024));
                                    }), 128)),
                                    (filteredTimeslots.value.length == 0 || _unref(cartItem).selectedTimeslot)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getResourceGroups)(__props.product, _unref(ResourceType).RESOURCE), (resourceGroup) => {
                                                return (_openBlock(), _createBlock(_component_v_container, {
                                                    class: "d-flex flex-wrap pa-0 pb-2",
                                                    key: resourceGroup.id
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createElementVNode("h4", _hoisted_5, _toDisplayString(resourceGroup.name), 1),
                                                        (Object.keys(_unref(availabilitiesWrapper)).length > 0)
                                                            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(resourceGroup.resources, (resource) => {
                                                                return (_openBlock(), _createBlock(_component_v_select, {
                                                                    key: resource.id,
                                                                    class: "v-col-12 compact pb-1 v-col-sm-6 v-col-md-12 v-col-lg-6",
                                                                    variant: "outlined",
                                                                    density: "compact",
                                                                    "hide-details": "",
                                                                    items: getResourceSelection(resource.id),
                                                                    label: resource.name,
                                                                    modelValue: _unref(cartItem).resourceReservations[resource.id],
                                                                    "onUpdate:modelValue": [($event) => ((_unref(cartItem).resourceReservations[resource.id]) = $event), ($event) => (onResourceSelected(resource, $event))],
                                                                    "data-cy": resource.name,
                                                                    placeholder: getResourceSelection(resource.id).length <= 1 ? 'Ausgebucht' : '',
                                                                    "persistent-placeholder": getResourceSelection(resource.id).length <= 1,
                                                                    disabled: getResourceSelection(resource.id).length <= 1
                                                                }, null, 8, ["items", "label", "modelValue", "onUpdate:modelValue", "data-cy", "placeholder", "persistent-placeholder", "disabled"]));
                                                            }), 128))
                                                            : (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                                key: 1,
                                                                class: "v-col-12",
                                                                indeterminate: ""
                                                            }))
                                                    ]),
                                                    _: 2
                                                }, 1024));
                                            }), 128)),
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getResources)(__props.product, _unref(ResourceType).RESOURCE), (resource) => {
                                                return (_openBlock(), _createBlock(_component_v_select, {
                                                    key: resource.id,
                                                    class: "v-col-12 compact pb-1",
                                                    variant: "outlined",
                                                    density: "compact",
                                                    "hide-details": "",
                                                    items: getResourceSelection(resource.id),
                                                    label: resource.name,
                                                    modelValue: _unref(cartItem).resourceReservations[resource.id],
                                                    "onUpdate:modelValue": [($event) => ((_unref(cartItem).resourceReservations[resource.id]) = $event), ($event) => (onResourceSelected(resource, $event))],
                                                    "data-cy": resource.name,
                                                    placeholder: getResourceSelection(resource.id).length <= 1 ? 'Ausgebucht' : '',
                                                    "persistent-placeholder": getResourceSelection(resource.id).length <= 1,
                                                    disabled: getResourceSelection(resource.id).length <= 1
                                                }, null, 8, ["items", "label", "modelValue", "onUpdate:modelValue", "data-cy", "placeholder", "persistent-placeholder", "disabled"]));
                                            }), 128))
                                        ], 64))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_btn, {
                                "data-cy": "productMetaButton",
                                class: "v-col-12",
                                onClick: click_button
                            }, {
                                default: _withCtx(() => [
                                    _renderSlot(_ctx.$slots, "button", {}, () => [
                                        _cache[2] || (_cache[2] = _createTextVNode(" Hinzufügen"))
                                    ])
                                ]),
                                _: 3
                            })
                        ]),
                        _: 3
                    }, 512)
                ]),
                _: 3
            }));
        };
    }
});
